import { useEffect, useState } from 'react'
import { Loader2, Sparkles, Send, CheckCircle2, AlertTriangle, RefreshCw, Lock, Download } from 'lucide-react'
import api from '../../services/api'
import { useTranslation } from 'react-i18next'

/**
 * DocumentFlowPanel — the last two steps of the Document Flow for one document.
 *
 *   upload (already done)  →  AI summary, reviewed & saved  →  submit  →  the assistant ingests it
 *
 * The summary is written automatically when the file is uploaded. This panel is where a person
 * reads it, corrects it and saves it — and only then can the file be sent. The gate is enforced
 * in the backend too; this UI just makes it obvious.
 *
 * ProTrack keeps the master copy. Submitting sends the assistant a COPY to ingest; everything the
 * assistant derives from it stays inside that product.
 */
export default function DocumentFlowPanel({ projectId, documentId }) {
  const { t } = useTranslation('platform')
  const [state, setState] = useState(null)
  const [summary, setSummary] = useState('')
  const [projects, setProjects] = useState([])
  const [target, setTarget] = useState('')
  const [busy, setBusy] = useState(null)   // 'save' | 'draft' | 'send' | 'check'
  const [error, setError] = useState(null)
  const [note, setNote] = useState(null)

  const base = `/projects/${projectId}/documents/${documentId}`

  const load = async () => {
    try {
      const { data } = await api.get(`${base}/flow`)
      setState(data)
      setSummary(data.summary || '')
      if (data.connection?.connected) {
        try {
          const { data: p } = await api.get('/platform/assistant/projects')
          setProjects(p.projects || [])
        } catch { /* the picker simply stays empty; the reason is shown below */ }
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load this document.')
    }
  }
  useEffect(() => { load() }, [projectId, documentId])

  if (error && !state) {
    return <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
  }
  if (!state) {
    return <p className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
  }

  const canEdit = state.my_level === 'edit'
  const conn = state.connection || {}

  /** A server error message, always as a string.
   *  FastAPI returns `detail` as an ARRAY of objects on a 422. Putting that straight into state
   *  and rendering it made React throw "Objects are not valid as a React child", which unmounted
   *  the whole panel — so a validation error appeared as a blank screen instead of a message. */
  const errText = (e, fallback) => {
    const d = e?.response?.data?.detail
    if (typeof d === 'string' && d.trim()) return d
    if (Array.isArray(d)) {
      const msgs = d.map(x => (typeof x === 'string' ? x : x?.msg)).filter(Boolean)
      if (msgs.length) return msgs.join('; ')
    }
    return fallback
  }

  const run = async (which, fn) => {
    setBusy(which); setError(null); setNote(null)
    try {
      const { data } = await fn()
      setNote(data.detail || null)
      if (data.summary !== undefined) setSummary(data.summary || '')
      setState(s => ({ ...s, ...data }))
    } catch (e) {
      setError(errText(e, 'That did not work.'))
    } finally { setBusy(null) }
  }

  const save = () => run('save', () => api.put(`${base}/summary`, { ai_summary: summary }))
  const draft = () => run('draft', () => api.post(`${base}/summary/regenerate`))
  const send = () => {
    if (!target) { setError('Choose which of the assistant’s projects this data belongs to.'); return }
    return run('send', () => api.post(`${base}/submit-to-assistant`, {
      assistant_project_id: Number(target), product_key: 'hfo',
    }))
  }
  const check = () => run('check', () => api.get(`${base}/assistant-status`))
  const markReady = () => run('ready', () => api.post(`${base}/mark-ready`, { product_key: 'hfo' }))
  const markHandedOver = () => {
    if (!window.confirm('Confirm you have uploaded this file into the assistant.\n\nProTrack records your confirmation — it cannot check the other side.')) return
    return run('done', () => api.post(`${base}/mark-handed-over`, {}))
  }

  // The zip needs the bearer token, so it cannot be a plain link — fetch it through the same
  // axios instance and hand the browser a blob.
  const downloadBundle = async (e) => {
    e?.preventDefault?.()
    setError(null)
    try {
      const res = await api.get(`${base}/handover-bundle`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      // Named from the document, not from the summary prose — the earlier version produced
      // filenames made of the first 40 characters of a sentence.
      a.download = `document-${documentId}-for-assistant.zip`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      // A failed blob download carries its JSON error in the blob body, not in data.detail.
      let msg = 'Could not download the file.'
      try {
        if (err.response?.data instanceof Blob) {
          const parsed = JSON.parse(await err.response.data.text())
          if (typeof parsed?.detail === 'string') msg = parsed.detail
        } else { msg = errText(err, msg) }
      } catch { /* keep the fallback */ }
      setError(msg)
    }
  }

  const dirty = (summary || '').trim() !== (state.summary || '').trim()

  return (
    <div className="space-y-4">
      {/* ── Step: the summary gate ─────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-indigo-500" /> Summary
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Written automatically from the file. Read it, correct anything wrong, then save — the
              assistant relies on this to understand what the data is.
            </p>
          </div>
          {state.summary_approved
            ? <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> Saved</span>
            : <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"><Lock className="w-3 h-3" /> Needs review</span>}
        </div>

        <textarea
          value={summary}
          onChange={e => setSummary(e.target.value)}
          disabled={!canEdit}
          rows={6}
          placeholder="What is this data, what does it cover, and what should the assistant know about it?"
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50"
        />

        {canEdit ? (
          <div className="flex flex-wrap gap-2 mt-2">
            <button onClick={save} disabled={busy === 'save' || !summary.trim()}
              className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg px-3 py-1.5">
              {busy === 'save' ? 'Saving…' : state.summary_approved && !dirty ? 'Saved' : 'Save summary'}
            </button>
            <button onClick={draft} disabled={busy === 'draft'}
              className="text-xs font-medium text-gray-600 hover:text-indigo-700 border border-gray-300 rounded-lg px-3 py-1.5">
              {busy === 'draft' ? 'Writing…' : 'Write a fresh draft'}
            </button>
            {state.summary_approved && dirty && (
              <span className="text-[11px] text-amber-700 self-center">{t('unsaved_changes')}</span>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-gray-400 mt-2">{t('read_not_change')}</p>
        )}
      </div>

      {/* ── Step: hand it to the assistant ─────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <Send className="w-4 h-4 text-indigo-500" /> Send to the assistant
        </p>
        <p className="text-xs text-gray-500 mt-0.5 mb-3">
          ProTrack keeps the original. The assistant receives a copy so it can answer questions from
          this data.
        </p>

        {/* ── Already handed over: the trail is closed ────────────────────── */}
        {state.is_handed_over ? (
          <div className="text-xs space-y-1.5">
            <p className="text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Handed over {state.assistant_submitted_at && new Date(state.assistant_submitted_at).toLocaleString()}
                {state.assistant_handover_mode === 'manual' ? ' — uploaded by a person' : ' — sent automatically'}.
                {state.assistant_message ? ` ${state.assistant_message}` : ''}
              </span>
            </p>
            {state.assistant_file_id && (
              <button onClick={check} disabled={busy === 'check'}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-indigo-700 border border-gray-300 rounded-lg px-3 py-1.5">
                <RefreshCw className={`w-3.5 h-3.5 ${busy === 'check' ? 'animate-spin' : ''}`} /> Check progress
              </button>
            )}
          </div>
        ) : !state.summary_approved ? (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5 shrink-0" /> Save the summary first — nothing leaves ProTrack
            without a human-checked description.
          </p>
        ) : (
          <div className="space-y-3">
            {/* ── Route 1 (default): a person carries it across ───────────── */}
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs font-semibold text-gray-800 mb-1">{t('hand_over_yourself')}</p>
              <p className="text-[11px] text-gray-500 mb-2.5">
                Download the file with its approved summary and upload it into the assistant. Nothing
                is transmitted from ProTrack, and the record here stays complete.
              </p>

              {!state.is_ready && canEdit && (
                <button onClick={markReady} disabled={busy === 'ready'}
                  className="w-full text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg py-2 mb-2">
                  {busy === 'ready' ? 'Marking…' : 'Mark ready & notify the administrator'}
                </button>
              )}

              {state.is_ready && (
                <p className="text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-lg px-2.5 py-1.5 mb-2">
                  Ready since {new Date(state.assistant_ready_at).toLocaleString()} — the administrator has been told.
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <button onClick={downloadBundle}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-indigo-700 border border-gray-300 rounded-lg px-3 py-1.5">
                  <Download className="w-3.5 h-3.5" /> Download file + summary
                </button>
                {canEdit && (
                  <button onClick={markHandedOver} disabled={busy === 'done'}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg px-3 py-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {busy === 'done' ? 'Recording…' : "I've uploaded it"}
                  </button>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-2">
                ProTrack can&rsquo;t see inside the assistant, so &ldquo;I&rsquo;ve uploaded it&rdquo; records your
                confirmation — it doesn&rsquo;t verify the other side.
              </p>
            </div>

            {/* ── Route 2 (optional): the automatic push, only when connected ── */}
            {conn.connected ? (
              <details className="rounded-lg border border-gray-200 p-3">
                <summary className="text-xs font-semibold text-gray-800 cursor-pointer">
                  Or send it automatically
                </summary>
                <div className="mt-2.5 space-y-2">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600 block mb-1">{t('which_projects')}</span>
                    <select value={target} onChange={e => setTarget(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm">
                      <option value="">{t('choose')}</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}{p.file_count != null ? ` · ${p.file_count} files` : ''}</option>
                      ))}
                    </select>
                  </label>
                  <button onClick={send} disabled={busy === 'send' || !canEdit}
                    className="w-full text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg py-2">
                    {busy === 'send' ? 'Sending…' : 'Send to the assistant'}
                  </button>
                </div>
              </details>
            ) : (
              <p className="text-[11px] text-gray-500 flex gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
                Automatic sending isn&rsquo;t available: {conn.reason}
              </p>
            )}
          </div>
        )}
      </div>

      {note && <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{note}</p>}
      {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
    </div>
  )
}
